import React from 'react';
import {FieldArray, FieldArrayConfig, FieldArrayRenderProps} from "formik";
import Disclosure from "components/Disclosure";
import Button from "components/Button";
import {PlusIcon, TagIcon, TrashIcon} from "@heroicons/react/solid";
import {InformationAPIData} from "types/Information";
import {FormBlock, FormSection} from "../index";
import {TextAreaField, TextField} from "../Field";
import {ToggleSwitch} from "../Control";
import LinkEditor from "../../LinkEditor";

export type InformationListFieldProps = FieldArrayConfig & {
  canLink?: boolean
}

function InformationListField({canLink, ...props}: InformationListFieldProps) {
  return (
    <FieldArray {...props}>
      {({push, remove, form}: FieldArrayRenderProps) => {

        const sectionsData = form.values[props.name] as Array<InformationAPIData> | undefined

        return (
          <>
            <ul className="space-y-4">
              {sectionsData?.map((section, index) => (
                <li key={index}>
                  <Disclosure name={section.name} defaultOpen={!section.id}>
                    <FormSection columns={6} isTop>

                      {/* Title */}
                      <FormBlock columns={3}>
                        <div className="mr-14 sm:mr-0">
                          <TextField
                            name={`${props.name}.${index}.name`}
                            label="Title *"
                            hint="250 chars max"
                            icon={TagIcon}
                            help="The section title when displayed"
                            maxLength={250}
                            autoComplete="off"
                          />
                        </div>
                      </FormBlock>

                      {/* Body */}
                      <FormBlock columns={6}>
                        <TextAreaField
                          name={`${props.name}.${index}.content`}
                          label="Body *"
                          hint="20,000 chars max"
                          help="The main content of the section when displayed "
                          autoComplete="off"
                          maxChars={20000}
                          rows={5}
                        />
                      </FormBlock>

                      {/* Link */}
                      {canLink &&
                        <FormBlock columns={6}>
                          {form => (
                            <>
                              <ToggleSwitch
                                label="Link"
                                checked={!!form.values[props.name][index].links?.length}
                                onChange={enabled => form.setFieldValue(`${props.name}.${index}.links`, enabled ? [{
                                  text: '',
                                  url: ''
                                }] : [])}
                              />

                              {!!form.values[props.name][index].links?.length &&
                                <div className="mt-2">
                                  <LinkEditor name={`${props.name}.${index}.links.0`} />
                                </div>
                              }
                            </>
                          )}
                        </FormBlock>
                      }

                      {/* Action Buttons */}
                      <FormBlock columns={6}>
                        <div className="flex justify-end pt-4 border-t border-gray-200">
                          <Button
                            size={Button.sizes.X_SMALL}
                            color={Button.colors.WHITE}
                            icon={TrashIcon}
                            onClick={() => remove(index)}
                          />
                        </div>
                      </FormBlock>
                    </FormSection>
                  </Disclosure>
                </li>
              ))}
            </ul>

            <div className={sectionsData?.length ? 'mt-4' : ''}>
              <Button
                color={Button.colors.SECONDARY}
                icon={PlusIcon}
                size={Button.sizes.TINY}
                onClick={() => push({name: '', content: ''})}
              >
                Add Section
              </Button>
            </div>
          </>
        )
      }}
    </FieldArray>
  );
}

export default InformationListField;